import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { DataTable, type Column } from "@/components/v2/DataTable";
import { StatusBadge } from "@/components/v2/StatusBadge";
import { EmptyState } from "@/components/v2/EmptyState";
import { BookingSidePanel } from "@/components/v2/BookingSidePanel";
import { formatPreferredDate } from "@/lib/booking-date";

type Row = {
  id?: string;
  customerName?: string;
  customerEmail?: string | null;
  preferredDate?: string | null;
  status?: string;
  paymentStatus?: string | null;
  price?: number | string;
  currency?: string;
};

/**
 * Service Detail · Bookings tab — Pencil `7xSZD`. Same DataTable +
 * BookingSidePanel pattern as `pages/Bookings.tsx` but scoped via
 * `?serviceId=` so the operator only sees bookings for this service.
 */
export function ServiceBookings() {
  const { id } = useParams<{ id: string }>();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["bookings", "service", id, page, pageSize],
    enabled: !!accessToken && !!id,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/bookings", {
          params: { query: { serviceId: id, page, size: pageSize } },
        }),
      ),
  });

  const cols: Column<Row>[] = [
    {
      key: "ref",
      header: "Ref",
      width: "92px",
      cell: (r) => (
        <Link
          to={`/bookings?focus=${r.id}`}
          onClick={(e) => {
            e.preventDefault();
            if (r.id) setOpenId(r.id);
          }}
          className="font-mono text-xs hover:underline"
        >
          #{r.id?.slice(0, 8)}
        </Link>
      ),
    },
    {
      key: "client",
      header: "Client",
      cell: (r) => (
        <div className="min-w-0">
          <p className="font-medium text-sm leading-tight truncate">
            {r.customerName || "(walk-in)"}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {r.customerEmail ?? "—"}
          </p>
        </div>
      ),
    },
    {
      key: "when",
      header: "Date",
      cell: (r) => (
        <span className="text-sm whitespace-nowrap">
          {r.preferredDate ? formatPreferredDate(r.preferredDate) : "—"}
        </span>
      ),
    },
    { key: "status", header: "Status", cell: (r) => <StatusBadge label={r.status ?? "—"} /> },
    {
      key: "payment",
      header: "Payment",
      cell: (r) => <StatusBadge label={r.paymentStatus ?? "—"} />,
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      cell: (r) => (
        <span className="font-semibold text-sm">
          {r.currency} {String(r.price)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {list.isError && (
        <p className="text-sm text-destructive">{(list.error as Error).message}</p>
      )}

      {list.data && (
        <DataTable
          columns={cols}
          rows={(list.data.content ?? []) as Row[]}
          rowKey={(r) => r.id ?? Math.random().toString()}
          onRowClick={(r) => {
            if (r.id) setOpenId(r.id);
          }}
          loading={list.isLoading}
          sortable
          resizable
          hideable
          height={500}
          pagination={{
            pageIndex: page,
            pageSize,
            pageCount: list.data.totalPages ?? 1,
            totalRows: list.data.totalElements,
            onPageChange: setPage,
            onPageSizeChange: (n) => {
              setPageSize(n);
              setPage(0);
            },
          }}
          emptyState={
            <div className="p-8">
              <EmptyState
                title="No bookings for this service"
                description="When clients book this service, they'll show up here."
              />
            </div>
          }
        />
      )}

      <BookingSidePanel bookingId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
